import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const NONE = "__none";

export const FieldWrap = ({ label, name, error, required, hint, className, children }) => (
  <div className={cn("space-y-1.5", className)}>
    <Label htmlFor={name} className="text-[13px] font-medium text-slate-700">
      {label}
      {required && <span className="ml-0.5 text-amber-600">*</span>}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
    {error && <p className="text-xs font-medium text-rose-600" data-testid={`error-${name}`}>{error}</p>}
  </div>
);

export const TextField = ({ name, label, form, set, errors = {}, required, hint, className, prefix, suffix, ...props }) => (
  <FieldWrap label={label} name={name} error={errors[name]} required={required} hint={hint} className={className}>
    <div className="relative">
      {prefix && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{prefix}</span>}
      <Input
        id={name}
        value={form[name] ?? ""}
        onChange={(e) => set(name, e.target.value)}
        data-testid={`field-${name}`}
        aria-invalid={!!errors[name]}
        className={cn("h-10 bg-white", prefix && "pl-7", suffix && "pr-10", errors[name] && "border-rose-400 focus-visible:ring-rose-300")}
        {...props}
      />
      {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>}
    </div>
  </FieldWrap>
);

export const MoneyField = (props) => <TextField prefix="$" inputMode="decimal" placeholder="0.00" {...props} />;

export const AreaField = ({ name, label, form, set, errors = {}, hint, className, rows = 3, ...props }) => (
  <FieldWrap label={label} name={name} error={errors[name]} hint={hint} className={className}>
    <Textarea id={name} rows={rows} value={form[name] ?? ""} onChange={(e) => set(name, e.target.value)} data-testid={`field-${name}`} className="bg-white" {...props} />
  </FieldWrap>
);

export const SelectField = ({ name, label, options, form, set, errors = {}, required, hint, className, placeholder = "Select…", clearable = true }) => {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const value = form[name];
  if (value && !opts.some((o) => o.value === value)) opts.push({ value, label: value });
  return (
    <FieldWrap label={label} name={name} error={errors[name]} required={required} hint={hint} className={className}>
      <Select value={value || undefined} onValueChange={(v) => set(name, v === NONE ? "" : v)}>
        <SelectTrigger id={name} data-testid={`field-${name}`} className={cn("h-10 bg-white", errors[name] && "border-rose-400")}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {clearable && value && <SelectItem value={NONE} className="text-slate-400">— Clear —</SelectItem>}
          {opts.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No options yet — add them in Settings</div>}
          {opts.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`option-${name}-${o.value}`}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldWrap>
  );
};

export const YesNo = ({ name, label, form, set, testId }) => (
  <FieldWrap label={label} name={name}>
    <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid={testId || `field-${name}`}>
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          onClick={() => set(name, v)}
          data-testid={`${testId || `field-${name}`}-${v ? "yes" : "no"}`}
          className={cn(
            "rounded px-4 py-1.5 text-sm font-medium transition-colors",
            form[name] === v ? (v ? "bg-emerald-600 text-white" : "bg-[#0E1E38] text-white") : "text-slate-600 hover:text-slate-900"
          )}
        >
          {v ? "Yes" : "No"}
        </button>
      ))}
    </div>
  </FieldWrap>
);
